import type { ColumnDef } from "@tanstack/react-table";
import { Badge, statusTone } from "@/components/ui";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  formatStatus,
} from "@/lib/format";

export type SyncRunRow = {
  id: number;
  source: string;
  trigger_type: string | null;
  job_kind: string | null;
  started_at: string;
  finished_at: string | null;
  status: string;
  rows_written: number | null;
  window_pulled: string | null;
  error_detail: string | null;
};

export type DailyActivityRow = {
  id: number;
  rep_id: number;
  team_id: number;
  activity_date: string;
  rep_name: string;
  team_name: string;
  region: string;
  total_calls: number | null;
  connected_calls: number | null;
  meetings_held: number | null;
  sequences: number | null;
  sync_run_id: number | null;
  updated_at: string | null;
};

export type PeriodMetricRow = {
  id: number;
  rep_id: number;
  team_id: number;
  rep_name: string;
  team_name: string;
  region: string;
  period_type: string;
  period_start: string;
  sales_amount: number | null;
  sales_margin: number | null;
  opps_generated_count: number | null;
  opps_generated_value: number | null;
  currency: string | null;
  sync_run_id: number | null;
  updated_at: string | null;
};

export type TeamPeriodMetricRow = {
  id: number;
  team_id: number;
  team_name: string;
  region: string;
  period_type: string;
  period_start: string;
  sales_amount: number | null;
  opps_generated_count: number | null;
  opps_generated_value: number | null;
  currency: string | null;
  sync_run_id: number | null;
  updated_at: string | null;
};

export type RepRow = {
  id: number;
  team_id: number;
  full_name: string;
  team_name: string;
  region: string;
  syncore_ref: string | null;
  hubspot_owner_id: string | null;
  active: boolean;
  start_date: string | null;
};

export type TeamRow = {
  id: number;
  name: string;
  department: string;
  region: string;
  syncore_ref: string | null;
  active: boolean;
};

export const syncRunColumns: ColumnDef<SyncRunRow, unknown>[] = [
  { accessorKey: "id", header: "ID", cell: ({ getValue }) => formatNumber(getValue<number>()) },
  { accessorKey: "source", header: "Source" },
  { accessorKey: "trigger_type", header: "Trigger" },
  { accessorKey: "job_kind", header: "Job" },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ getValue }) => {
      const value = getValue<string>();
      return <Badge tone={statusTone(value)}>{formatStatus(value)}</Badge>;
    },
  },
  {
    accessorKey: "rows_written",
    header: "Rows",
    cell: ({ getValue }) => formatNumber(getValue<number | null>()),
  },
  { accessorKey: "window_pulled", header: "Window" },
  {
    accessorKey: "started_at",
    header: "Started",
    cell: ({ getValue }) => formatDateTime(getValue<string>()),
  },
  {
    accessorKey: "finished_at",
    header: "Finished",
    cell: ({ getValue }) => formatDateTime(getValue<string | null>()),
  },
  {
    accessorKey: "error_detail",
    header: "Error",
    cell: ({ getValue }) => {
      const value = getValue<string | null>();
      return value ? (
        <span className="max-w-xs truncate text-[var(--color-danger)]">{value}</span>
      ) : (
        "—"
      );
    },
  },
];

export const dailyActivityColumns: ColumnDef<DailyActivityRow, unknown>[] = [
  {
    accessorKey: "activity_date",
    header: "Date",
    cell: ({ getValue }) => formatDate(getValue<string>()),
  },
  { accessorKey: "rep_name", header: "Rep" },
  { accessorKey: "team_name", header: "Team" },
  { accessorKey: "region", header: "Region" },
  {
    accessorKey: "total_calls",
    header: "Total calls",
    cell: ({ getValue }) => formatNumber(getValue<number | null>()),
  },
  {
    accessorKey: "connected_calls",
    header: "Connected",
    cell: ({ getValue }) => formatNumber(getValue<number | null>()),
  },
  {
    accessorKey: "meetings_held",
    header: "Meetings",
    cell: ({ getValue }) => formatNumber(getValue<number | null>()),
  },
  {
    accessorKey: "sequences",
    header: "Sequences",
    cell: ({ getValue }) => formatNumber(getValue<number | null>()),
  },
  {
    accessorKey: "sync_run_id",
    header: "Sync run",
    cell: ({ getValue }) => formatNumber(getValue<number | null>()),
  },
];

export const periodMetricColumns: ColumnDef<PeriodMetricRow, unknown>[] = [
  { accessorKey: "period_type", header: "Period" },
  {
    accessorKey: "period_start",
    header: "Start",
    cell: ({ getValue }) => formatDate(getValue<string>()),
  },
  { accessorKey: "rep_name", header: "Rep" },
  { accessorKey: "team_name", header: "Team" },
  { accessorKey: "region", header: "Region" },
  {
    accessorKey: "sales_amount",
    header: "Sales",
    cell: ({ getValue }) => formatCurrency(getValue<number | null>()),
  },
  {
    accessorKey: "sales_margin",
    header: "Margin",
    cell: ({ getValue }) => formatCurrency(getValue<number | null>()),
  },
  {
    accessorKey: "opps_generated_count",
    header: "Opps #",
    cell: ({ getValue }) => formatNumber(getValue<number | null>()),
  },
  {
    accessorKey: "opps_generated_value",
    header: "Opps $",
    cell: ({ getValue }) => formatCurrency(getValue<number | null>()),
  },
  { accessorKey: "currency", header: "Currency" },
];

export const teamPeriodMetricColumns: ColumnDef<TeamPeriodMetricRow, unknown>[] = [
  { accessorKey: "period_type", header: "Period" },
  {
    accessorKey: "period_start",
    header: "Start",
    cell: ({ getValue }) => formatDate(getValue<string>()),
  },
  { accessorKey: "team_name", header: "Team" },
  { accessorKey: "region", header: "Region" },
  {
    accessorKey: "sales_amount",
    header: "Sales",
    cell: ({ getValue }) => formatCurrency(getValue<number | null>()),
  },
  {
    accessorKey: "opps_generated_count",
    header: "Opps #",
    cell: ({ getValue }) => formatNumber(getValue<number | null>()),
  },
  {
    accessorKey: "opps_generated_value",
    header: "Opps $",
    cell: ({ getValue }) => formatCurrency(getValue<number | null>()),
  },
  { accessorKey: "currency", header: "Currency" },
];

export const repColumns: ColumnDef<RepRow, unknown>[] = [
  { accessorKey: "full_name", header: "Name" },
  { accessorKey: "team_name", header: "Team" },
  { accessorKey: "region", header: "Region" },
  { accessorKey: "syncore_ref", header: "Syncore ref" },
  { accessorKey: "hubspot_owner_id", header: "HubSpot owner" },
  {
    accessorKey: "active",
    header: "Active",
    cell: ({ getValue }) => (getValue<boolean>() ? "Yes" : "No"),
  },
  {
    accessorKey: "start_date",
    header: "Start",
    cell: ({ getValue }) => formatDate(getValue<string | null>()),
  },
];

export const teamColumns: ColumnDef<TeamRow, unknown>[] = [
  { accessorKey: "name", header: "Name" },
  { accessorKey: "department", header: "Department" },
  { accessorKey: "region", header: "Region" },
  { accessorKey: "syncore_ref", header: "Syncore ref" },
  {
    accessorKey: "active",
    header: "Active",
    cell: ({ getValue }) => (getValue<boolean>() ? "Yes" : "No"),
  },
];
